import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Smartphone, Loader2, Download, CheckCircle2, AlertCircle, RefreshCw, Scissors, Play, Palette,
  Columns2, Presentation, ArrowRight,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { SectionCard, Field, Skeleton } from './ui';
import { ServedModelBadge } from './ServedModelBadge';
import { buildSrcdoc } from './prototypePreview';
import { CreditBanner, isCreditError } from './CreditBanner';
import { buildZipBlob } from '../../lib/zipStore';
import { PipelineRail, RailRow } from './PipelineRail';

// Rotating micro-status for the active rail row: advances through the pool and HOLDS on the
// last line — a repeated message is what makes users read "hung" (Ali, 2026-08-16).
function useMicro(pool, active) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (!active || !Array.isArray(pool) || pool.length < 2) return undefined;
    const id = setInterval(() => setIdx((i) => Math.min(i + 1, pool.length - 1)), 7000);
    return () => clearInterval(id);
  }, [active, pool]);
  if (!active || !Array.isArray(pool) || pool.length === 0) return null;
  return pool[Math.min(idx, pool.length - 1)];
}

const themeLabel = (theme, lang) =>
  theme?.label?.[lang] || theme?.label?.en || theme?.key || '';

// SVG string -> PNG blob, client-side (canvas), no dependencies. The pitch slides are
// authored at 1280x720 by the assembler.
function svgToPngBlob(svgText) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1280;
      canvas.height = 720;
      canvas.getContext('2d').drawImage(img, 0, 0, 1280, 720);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png encode failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('svg render failed'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  });
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// The MVP cut is the USER'S decision (Ali, 2026-08-13): every item can be moved between
// "in the prototype" and "kept aside"; a changed cut unlocks the re-plan button, which
// regenerates the structure honoring the choices — tokens only, never a second flat fee.
// Standalone model picker (no button): the choice rides along on every call of the run —
// first generation, re-plans, screen fills. Same keyless-aware list RegenerateWithModel uses.
const TIER_KEY = { frontier: 'prototype.tierFrontier', capable: 'prototype.tierCapable',
  basic: 'prototype.tierBasic', unknown: 'prototype.tierUnknown' };
export const isCapableTier = (tier) => tier === 'capable' || tier === 'frontier';

function ModelPicker({ value, onChange, busy, testId, onModels }) {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  useEffect(() => {
    let live = true;
    // Paint from the fast list first (names + tiers), then annotate with key availability.
    // The tier gate only needs the fast list, so bespoke unlocks without waiting on keys.
    api.listModelsFast()
      .then((r) => { if (!live) return; const m = r?.models || []; setModels(m); onModels?.(m); })
      .catch(() => {})
      .finally(() => {
        api.modelKeyAvailability()
          .then((r) => { if (!live) return; const m = r?.models || []; setModels(m); onModels?.(m); })
          .catch(() => {});
      });
    return () => { live = false; };
  }, [onModels]);
  if (!models.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <span className="text-xs font-medium text-muted-foreground"><T k="prototype.modelLabel" /></span>
      <select
        className="text-xs border border-border rounded-md px-2 py-1 bg-card text-foreground"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={busy}
        data-testid={`${testId}-select`}
      >
        <option value=""><T k="walletCore.modelDefault" /></option>
        {models.map((m) => (
          <option key={m.model_id} value={m.model_id}>
            {(m.display_label || m.model_id)
              + ` · ${t(TIER_KEY[m.capability_tier] || TIER_KEY.unknown)}`
              + (m.has_key === false ? ` · ${t('settings.modelNoKey')}` : '')}
          </option>
        ))}
      </select>
    </div>
  );
}

function ModeCards({ mode, onMode, models, modelId, onModel, busy }) {
  const { t } = useTranslation();
  // Until the model list arrives the tier is UNKNOWN, not "incapable" — saying "not available
  // with this model" while still loading is a false statement, and the exact kind of thing that
  // reads as broken (found in the 2026-08-17 verification pass).
  const loading = models.length === 0;
  const sel = models.find((m) => m.model_id === modelId);
  // No explicit pick = the account/platform default, whose tier we cannot know here. Treat
  // that as "not proven capable" — honest, and the server refuses it the same way.
  const capable = isCapableTier(sel?.capability_tier);
  const capableModels = models.filter((m) => isCapableTier(m.capability_tier));
  // The approved mock: each card carries a real PREVIEW of what that mode produces, so the
  // difference is visible before spending anything — block = tidy grey blocks, bespoke = a
  // designed surface with its own palette and motion.
  const BlockPreview = () => (
    <div className="flex h-24 flex-col gap-1.5 rounded-lg bg-muted/60 p-2">
      <div className="flex h-4 items-center gap-1.5 rounded bg-card px-1.5">
        <span className="h-2 w-2 rounded-sm bg-border" />
        <span className="h-1 w-10 rounded bg-border" />
      </div>
      <div className="flex gap-1.5">
        <span className="h-7 flex-1 rounded bg-card" />
        <span className="h-7 flex-1 rounded bg-card" />
        <span className="h-7 flex-1 rounded bg-card" />
      </div>
      <span className="flex-1 rounded bg-card" />
    </div>
  );
  const BespokePreview = () => (
    <div className="relative h-24 overflow-hidden rounded-lg p-2"
      style={{ background: 'linear-gradient(150deg,#26215C,#4A1B0C)' }}>
      <span className="absolute -end-6 -top-10 h-24 w-24 rounded-full"
        style={{ background: 'rgba(243,201,123,.22)' }} />
      <span className="inline-block rounded-full px-2 py-0.5 text-[9px]"
        style={{ background: 'rgba(243,201,123,.16)', color: '#F3C97B' }}>{t('prototype.modePreviewTag')}</span>
      <div className="relative mt-1.5 rounded-md p-1.5"
        style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)' }}>
        <div className="mb-1 flex items-center gap-1.5">
          <span className="h-3 w-3 rounded" style={{ background: 'linear-gradient(140deg,#F3C97B,#E0709B)' }} />
          <span className="h-1 w-8 rounded" style={{ background: 'rgba(255,255,255,.5)' }} />
        </div>
        <span className="mb-0.5 block h-1 w-full rounded" style={{ background: 'rgba(255,255,255,.28)' }} />
        <span className="block h-1 w-3/4 rounded" style={{ background: 'rgba(255,255,255,.28)' }} />
      </div>
      <div className="relative mt-1.5 h-3.5 rounded"
        style={{ background: 'linear-gradient(120deg,#F3C97B,#E0709B)' }} />
    </div>
  );
  const Card = ({ id, preview, title, desc, time, credits, foot, locked, badge }) => (
    <button
      type="button"
      disabled={busy || locked}
      onClick={() => !locked && onMode(id)}
      data-testid={`prototype-mode-${id}`}
      className={`relative overflow-hidden rounded-xl border text-start transition-all ${
        locked
          ? 'border-dashed border-border bg-muted/30 opacity-80 cursor-not-allowed'
          : mode === id
            ? 'border-2 border-primary bg-card shadow-lg'
            : 'border-border bg-card hover:border-primary/60 hover:shadow-md'}`}
    >
      <div className="p-2.5 pb-0">{preview}</div>
      <div className="p-3 pt-2.5">
        <p className="flex items-center gap-2 font-medium">
          {title}
          {badge}
          {mode === id && !locked && (
            <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
              <CheckCircle2 className="h-3 w-3" /> {t('prototype.modeSelected')}
            </span>
          )}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{desc}</p>
        <div className="mt-2.5 flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">⏱ {time}</span>
          <span className="text-muted-foreground">◎ {credits}</span>
          {foot}
        </div>
      </div>
    </button>
  );
  return (
    <div className="flex flex-col gap-3" data-testid="prototype-mode-cards">
      <p className="text-sm font-medium"><T k="prototype.modeTitle" /></p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Card
          id="block"
          preview={<BlockPreview />}
          title={t('prototype.modeBlockTitle')}
          desc={t('prototype.modeBlockDesc')}
          time={t('prototype.modeBlockTime')}
          credits={t('prototype.modeBlockCost')}
          foot={<span className="text-success">✓ {t('prototype.modeAnyModel')}</span>}
        />
        <Card
          id="bespoke"
          preview={<BespokePreview />}
          badge={<span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{t('prototype.modeNew')}</span>}
          title={t('prototype.modeBespokeTitle')}
          desc={t('prototype.modeBespokeDesc')}
          time={t('prototype.modeBespokeTime')}
          credits={t('prototype.modeBespokeCost')}
          locked={loading || !capable}
          foot={loading
            ? <span className="text-muted-foreground">{t('prototype.modeChecking')}</span>
            : capable
              ? <span className="text-primary">✦ {t('prototype.modeNeedsCapable')}</span>
              : <span className="text-warning">{t('prototype.modeLockedWeak')}</span>}
        />
      </div>
      {!loading && !capable && capableModels.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="prototype-capable-hint">
          <p className="text-xs text-muted-foreground mb-2"><T k="prototype.modeCapableList" /></p>
          <div className="flex flex-wrap gap-2">
            {capableModels.map((m) => (
              <Button key={m.model_id} size="sm" variant="outline" disabled={busy}
                onClick={() => { onModel(m.model_id); onMode('bespoke'); }}
                data-testid={`prototype-pick-capable-${m.model_id}`}>
                {m.display_label || m.model_id}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MvpCutCard({ mvpCut, busy, onReplan, onDirtyChange }) {
  const { t } = useTranslation();
  const [moved, setMoved] = useState({}); // item -> 'in' | 'out' (overrides of the model's cut)
  const placed = (item, origin) => moved[item] ?? origin;
  const inList = [];
  const outList = [];
  (mvpCut?.included || []).forEach((item) => (placed(item, 'in') === 'in' ? inList : outList).push({ item }));
  (mvpCut?.dropped || []).forEach((d) => (placed(d.item, 'out') === 'in' ? inList : outList).push(d));
  const changed = Object.keys(moved).some((k) => {
    const origin = (mvpCut?.included || []).includes(k) ? 'in' : 'out';
    return moved[k] !== origin;
  });
  // A cut with pending edits makes the CURRENT screens stale — the parent must not let the
  // user fill screens that the re-plan is about to replace (Ali, 2026-08-13).
  useEffect(() => { onDirtyChange?.(changed); }, [changed, onDirtyChange]);
  const toggle = (item, to) => setMoved((prev) => ({ ...prev, [item]: to }));

  return (
    <SectionCard title={t('prototype.cutTitle')} testId="prototype-mvp-cut">
      <p className="text-sm text-muted-foreground flex items-center gap-2">
        <Scissors className="h-4 w-4" /> {t('prototype.cutHelpDecide')}
      </p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={<T k="prototype.cutIncluded" />}>
          <ul className="space-y-1.5">
            {inList.map(({ item }) => (
              <li key={item} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                <span className="flex-1 min-w-0">{item}</span>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => toggle(item, 'out')}
                  data-testid={`cut-out-${item}`}>
                  {t('prototype.cutPutAside')}
                </Button>
              </li>
            ))}
            {inList.length === 0 && <p className="text-sm text-muted-foreground"><T k="prototype.cutNothingIn" /></p>}
          </ul>
        </Field>
        <Field label={<T k="prototype.cutDropped" />}>
          {outList.length === 0
            ? <p className="text-sm text-muted-foreground"><T k="prototype.cutNothingDropped" /></p>
            : (
              <ul className="space-y-1.5">
                {outList.map((d) => (
                  <li key={d.item} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-foreground">{d.item}</span>
                      {d.reason && <span className="text-muted-foreground"> — {d.reason}</span>}
                    </span>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => toggle(d.item, 'in')}
                      data-testid={`cut-in-${d.item}`}>
                      {t('prototype.cutBringBack')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
        </Field>
      </div>
      {changed && (
        <div className="flex items-center justify-between gap-4 flex-wrap pt-2 border-t border-border">
          <p className="text-sm text-muted-foreground"><T k="prototype.replanHint" /></p>
          <Button
            disabled={busy}
            onClick={() => onReplan({
              include: inList.map((x) => x.item),
              exclude: outList.map((x) => x.item),
            })}
            data-testid="prototype-replan-button"
          >
            {busy
              ? (<><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('prototype.replanning')}</>)
              : (<><Scissors className="h-4 w-4 me-2" /> {t('prototype.replan')}</>)}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function ScreenProgress({ spec, tasks, screenErrors, creditWait, doneIds, onRetry }) {
  const { t } = useTranslation();
  const lang = spec.primary_lang;
  return (
    <SectionCard title={t('prototype.screensTitle')} testId="prototype-screens">
      <div className="flex flex-col gap-2">
        {(spec.screens || []).map((s) => {
          const st = tasks?.find((tk) => tk.id === s.id)?.status;
          const err = screenErrors?.[s.id];
          const waiting = creditWait?.includes(s.id) && !s.filled;
          let badge;
          if (s.filled || doneIds?.has(s.id)) badge = <CheckCircle2 className="h-5 w-5 text-success" />;
          else if (waiting) badge = <AlertCircle className="h-5 w-5 text-warning" />;
          else if (err) badge = <AlertCircle className="h-5 w-5 text-destructive" />;
          else if (st === 'active') badge = <Loader2 className="h-5 w-5 text-foreground animate-spin" />;
          else if (st === 'pending') badge = <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />;
          else badge = <span className="h-5 w-5 rounded-full border border-border inline-block" />;
          return (
            <div key={s.id} className="flex items-center gap-3 p-3 border border-border rounded-xl bg-muted" data-testid={`prototype-screen-${s.id}`}>
              <span className="shrink-0 flex items-center">{badge}</span>
              <span className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
                {s.title?.[lang] || s.title?.en || s.id}
              </span>
              {waiting && (
                <span className="text-xs font-medium text-warning" data-testid={`prototype-credit-wait-${s.id}`}>
                  {t('prototype.creditWaitRow')}
                </span>
              )}
              {typeof s.build_step_n === 'number' && (
                <span className="text-xs text-muted-foreground font-data" data-testid={`prototype-step-map-${s.id}`}>
                  {t('prototype.builtByStep', { n: s.build_step_n })}
                </span>
              )}
              {err && (
                <Button size="sm" variant="outline" onClick={() => onRetry(s.id)} data-testid={`prototype-retry-${s.id}`}>
                  {t('prototype.retryScreen')}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

async function runScreenFills({ projectId, ids, abortRef, setTasks, setScreenErrors, setCreditWait, onFilled, t, opts = {} }) {
  const failed = [];
  const creditBlocked = [];
  for (const id of ids) {
    if (abortRef.current) return { aborted: true, failed, creditBlocked };
    setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'active' } : tk)) ?? prev);
    try {
      const res = await api.appMockScreen(projectId, id, opts);
      onFilled(id, res);
      setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'done' } : tk)) ?? prev);
      setScreenErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev }; delete next[id]; return next;
      });
      setCreditWait((prev) => prev.filter((x) => x !== id));
    } catch (e) {
      failed.push(id);
      // Out of credits is NOT an error state — it is "waiting for credit" (Ali's approved
      // mock, 2026-08-16): paid screens stay green, blocked ones wait, the banner is loud.
      if (e?.status === 402) {
        creditBlocked.push(id);
        setCreditWait((prev) => (prev.includes(id) ? prev : [...prev, id]));
        setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'credit' } : tk)) ?? prev);
      } else {
        const msg = e?.message || 'Generation failed';
        setScreenErrors((prev) => ({ ...prev, [id]: msg }));
        setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'error' } : tk)) ?? prev);
        toast.error(t('prototype.screenError', { id, message: msg.length > 160 ? `${msg.slice(0, 160)}…` : msg }));
      }
    }
  }
  return { aborted: false, failed, creditBlocked };
}

export default function PrototypeStage({ projectId, hasIdea, onChanged, servedModels, projectName }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);       // { app_mock, version_no, themes }
  const [cutDirty, setCutDirty] = useState(false); // pending cut edits → screens are stale
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [screenErrors, setScreenErrors] = useState({});
  const [creditWait, setCreditWait] = useState([]); // screens paused on 402 — waiting, not failed
  const [previewTheme, setPreviewTheme] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planCredit, setPlanCredit] = useState(false); // plan-level 402 → banner, not error card
  const [planWarnings, setPlanWarnings] = useState(null); // { missing_features, non_persian_titles }
  const [exportingPitch, setExportingPitch] = useState(false);
  const [compareTheme, setCompareTheme] = useState(null); // null = compare pane hidden
  const [compareDoc, setCompareDoc] = useState(null);
  // The model the user explicitly regenerated with; replan and screen fills reuse it so one
  // run stays on one model (the free default produces markedly weaker structures).
  const [modelId, setModelId] = useState(null);
  const [mode, setMode] = useState('block');   // block | bespoke (Ali's approved cards)
  const [models, setModels] = useState([]);   // for the tier gate on the bespoke card
  // Optional product vision (free text) — feeds the server's DESIGN pass as binding input.
  const [vision, setVision] = useState('');
  // The EDITABLE copy of the model-written design. Diverging from spec.design reveals the
  // "re-structure from my text" button; the edited text becomes the authoritative design.
  const [designDraft, setDesignDraft] = useState('');
  // DESIGN-FIRST (2026-08-16): the design arrives alone in one short call and waits here,
  // editable, until the user builds from it. { design, pages, regenerate } — regenerate
  // remembers whether this run started as a paid re-run so the structure step charges right.
  const [pendingDesign, setPendingDesign] = useState(null);
  // Which theater stage is live: null | 'design' | 'structure' (screens progress rides tasks).
  const [phase, setPhase] = useState(null);
  // Final elapsed seconds per finished stage — the small green chips on done rail rows.
  const [finalTimes, setFinalTimes] = useState({});
  const stageStartRef = useRef(null);
  const abortRef = useRef(false);
  const startStageClock = () => { stageStartRef.current = Date.now(); };
  const stopStageClock = (key) => {
    if (!stageStartRef.current) return;
    const sec = Math.floor((Date.now() - stageStartRef.current) / 1000);
    setFinalTimes((prev) => ({ ...prev, [key]: sec }));
    stageStartRef.current = null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.out('get_app_mock', { project_id: projectId });
      setData(r);
      return r;
    } catch (e) { toast.error(e.message); return null; } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => {
    abortRef.current = false;
    load();
    return () => { abortRef.current = true; };
  }, [load]);

  const spec = data?.app_mock?.spec || null;
  // Server-side bespoke progress (survives an app restart mid-run — every finished screen was
  // saved and is never re-charged on a re-run).
  const bespokeDone = useMemo(
    () => new Set(data?.app_mock?.bespoke?.screens_done || []),
    [data],
  );
  const bespokeShellDone = !!data?.app_mock?.bespoke?.has_shell;
  const bespokeRemaining = (spec?.screens || []).filter((x) => !bespokeDone.has(x.id)).length;
  // "Interrupted" = the server holds real work but the app is not finished. This is the state
  // an app restart leaves behind, and it must read as resumable, not as a fresh start.
  const bespokePartial = (bespokeShellDone || bespokeDone.size > 0) && bespokeRemaining > 0;
  const versionNo = data?.version_no || null;
  const themes = data?.themes || [];
  const effTheme = previewTheme || spec?.theme_key || null;
  const uiLang = ['en', 'fa', 'es'].includes(i18n.language) ? i18n.language : 'en';
  const unfilled = (spec?.screens || []).filter((s) => !s.filled).map((s) => s.id);

  // Rail statuses + rotating micro-status pools (memoized: a fresh t() array each render
  // would reset the rotation).
  const poolDesign = useMemo(() => t('progress.prototype.design', { returnObjects: true }), [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps
  const poolStructure = useMemo(() => t('progress.prototype.structure', { returnObjects: true }), [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps
  const poolScreens = useMemo(() => t('progress.prototype.screens', { returnObjects: true }), [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps
  const designMicro = useMicro(poolDesign, phase === 'design');
  const structureMicro = useMicro(poolStructure, phase === 'structure');
  const screensMicro = useMicro(poolScreens, !!tasks);
  const designStatus = phase === 'design' ? 'active' : (pendingDesign || spec?.design) ? 'done' : 'pending';
  const structureStatus = phase === 'structure' ? 'active' : spec ? 'done' : 'pending';
  const screensStatus = tasks ? 'active' : (spec && versionNo && unfilled.length === 0) ? 'done' : 'pending';

  // Keep the editable draft in sync with the authoritative design whenever a plan lands.
  const specDesign = spec?.design || '';
  useEffect(() => { setDesignDraft(specDesign); }, [specDesign]);

  // The preview reloads whenever a completed version or theme choice changes.
  useEffect(() => {
    if (!versionNo) { setPreviewDoc(null); return; }
    let stale = false;
    setPreviewLoading(true);
    api.appMockFiles(projectId, effTheme || undefined)
      .then((r) => { if (!stale) setPreviewDoc(buildSrcdoc(r.files || {})); })
      .catch((e) => { if (!stale) toast.error(e.message); })
      .finally(() => { if (!stale) setPreviewLoading(false); });
    return () => { stale = true; };
  }, [projectId, versionNo, effTheme]);

  // Second preview for the A/B theme comparison — loads only while the pane is open.
  useEffect(() => {
    if (!versionNo || !compareTheme) { setCompareDoc(null); return; }
    let stale = false;
    api.appMockFiles(projectId, compareTheme)
      .then((r) => { if (!stale) setCompareDoc(buildSrcdoc(r.files || {})); })
      .catch((e) => { if (!stale) toast.error(e.message); });
    return () => { stale = true; };
  }, [projectId, versionNo, compareTheme]);

  // Pitch deck: the assembler ships pitch/*.svg inside the file tree; export converts each to
  // PNG client-side (canvas) and downloads them — slides ready for any deck tool.
  const exportPitch = async () => {
    setExportingPitch(true);
    try {
      const r = await api.appMockFiles(projectId, effTheme || undefined);
      const entries = Object.entries(r.files || {}).filter(([p]) => p.startsWith('pitch/') && p.endsWith('.svg'));
      if (!entries.length) { toast.error(t('prototype.pitchNone')); return; }
      // One zip, not a burst of downloads: every slide as PNG inside <name>-pitch.zip.
      const zipEntries = [];
      for (const [path, svg] of entries) {
        const png = await svgToPngBlob(svg);
        zipEntries.push({
          name: path.replace('pitch/', '').replace('.svg', '.png'),
          data: new Uint8Array(await png.arrayBuffer()),
        });
      }
      const zipName = `${(spec?.product_name || 'app').replace(/[^\p{L}\p{N}]+/gu, '-')}-pitch.zip`;
      saveBlob(buildZipBlob(zipEntries), zipName);
      toast.success(t('prototype.pitchDone', { count: entries.length }));
    } catch (e) { toast.error(e.message); } finally { setExportingPitch(false); }
  };

  const fillScreens = async (ids, opts = {}) => {
    startStageClock();
    setTasks(ids.map((id) => ({ id, status: 'pending' })));
    const { aborted, failed, creditBlocked } = await runScreenFills({
      projectId,
      ids,
      abortRef,
      setTasks,
      setScreenErrors,
      setCreditWait,
      t,
      opts: modelId ? { model_id: modelId, ...opts } : opts,
      onFilled: (id, res) => {
        setData((prev) => {
          if (!prev?.app_mock?.spec) return prev;
          const nextSpec = {
            ...prev.app_mock.spec,
            screens: prev.app_mock.spec.screens.map((s) => (s.id === id ? { ...s, ...res.filled_screen ?? res.screen } : s)),
          };
          return {
            ...prev,
            app_mock: { ...prev.app_mock, spec: nextSpec },
            version_no: res.done && res.version_no ? res.version_no : prev.version_no,
          };
        });
      },
    });
    stopStageClock('screens');
    setTasks(null);
    if (aborted) return;
    await load();
    onChanged?.();
    if (failed.length === 0) toast.success(t('prototype.ready'));
    else if (creditBlocked.length === failed.length) { /* the banner says it all — no toast noise */ }
    else toast.warning(t('prototype.partial', { failed: failed.length }));
  };

  // PHASE A of design-first: one short call writes the design; it lands on screen editable
  // within ~20-40s instead of one long silent call that reads as a crash (Ali, 2026-08-16).
  const runBespoke = async (resume = false) => {
    const all = (spec?.screens || []).map((x) => x.id);
    // Resume skips what the server already has. Those calls are idempotent anyway (they return
    // without charging), but not asking at all is faster and clearer.
    const ids = resume ? all.filter((id) => !bespokeDone.has(id)) : all;
    if (!ids.length) return;
    setScreenErrors({});
    setPhase('screens');
    startStageClock();
    setTasks([
      { id: '__shell__', status: resume && bespokeShellDone ? 'done' : 'active' },
      ...ids.map((id) => ({ id, status: 'pending' })),
    ]);
    const opts = modelId ? { model_id: modelId } : {};
    try {
      await api.appBespokeShell(projectId, opts);
    } catch (e) {
      stopStageClock('screens'); setTasks(null);
      if (isCreditError(e)) { setPlanCredit(true); return; }
      // The tier gate answers 409 with the capable models attached — say so plainly.
      if (e?.status === 409) { setPlanError(t('prototype.bespokeNeedsCapable')); return; }
      setPlanError(e?.message || 'failed'); return;
    }
    setTasks((prev) => prev?.map((tk) => (tk.id === '__shell__' ? { ...tk, status: 'done' } : tk)) ?? prev);
    for (const id of ids) {
      if (abortRef.current) break;
      setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'active' } : tk)) ?? prev);
      try {
        await api.appBespokeScreen(projectId, id, opts);
        setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'done' } : tk)) ?? prev);
      } catch (e) {
        if (isCreditError(e)) {
          setCreditWait((prev) => (prev.includes(id) ? prev : [...prev, id]));
          setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'credit' } : tk)) ?? prev);
          break;
        }
        setScreenErrors((prev) => ({ ...prev, [id]: e?.message || 'failed' }));
        setTasks((prev) => prev?.map((tk) => (tk.id === id ? { ...tk, status: 'error' } : tk)) ?? prev);
      }
    }
    stopStageClock('screens');
    setTasks(null);
    await load();
    onChanged?.();
  };

  const startDesign = async (regenerate = false, overrideModelId = modelId) => {
    try {
      setScreenErrors({});
      setPlanError(null);
      setPlanCredit(false);
      setPlanWarnings(null);
      setPhase('design');
      startStageClock();
      setPlanning(true);
      const modelOpts = overrideModelId ? { model_id: overrideModelId } : {};
      const visionOpts = vision.trim() ? { vision: vision.trim() } : {};
      const r = await api.appMockPlan(projectId, {
        design_only: true, ...(regenerate ? { regenerate: true } : {}), ...modelOpts, ...visionOpts,
      });
      setPendingDesign({ design: r.design || '', pages: r.pages || [], regenerate });
      setDesignDraft(r.design || '');
      stopStageClock('design');
      setPhase(null);
      setPlanning(false);
    } catch (e) {
      setPhase(null);
      setPlanning(false);
      if (isCreditError(e)) setPlanCredit(true);
      else {
        setPlanError({ message: e?.message || 'Generation failed', details: [] });
        toast.error(e.message);
      }
    }
  };

  const generate = async (regenerate = false, replanDirectives = null, overrideModelId = modelId, designOverride = null, designPages = null) => {
    try {
      setScreenErrors({});
      setPlanError(null);
      setPlanCredit(false);
      setPlanWarnings(null);
      setPhase('structure');
      startStageClock();
      setPlanning(true);
      const modelOpts = overrideModelId ? { model_id: overrideModelId } : {};
      const visionOpts = vision.trim() ? { vision: vision.trim() } : {};
      // An edited design re-structures fee-free (replan) UNLESS this run began as a paid
      // regenerate — then the structure step carries the regenerate flag and its fee.
      const designOpts = designOverride
        ? {
          design_override: designOverride,
          ...(designPages?.length ? { design_pages: designPages } : {}),
          ...(regenerate ? { regenerate: true } : { replan: true }),
        }
        : {};
      const plan = await api.appMockPlan(
        projectId,
        replanDirectives
          ? { replan: true, directives: replanDirectives, ...modelOpts, ...visionOpts, ...designOpts }
          : (designOverride
            ? { ...modelOpts, ...visionOpts, ...designOpts }
            : (regenerate
              ? { regenerate: true, ...modelOpts, ...visionOpts }
              : { ...modelOpts, ...visionOpts })),
      );
      stopStageClock('structure');
      setPhase(null);
      setPendingDesign(null);
      setPlanning(false);
      setData((prev) => ({
        ...(prev || {}),
        app_mock: {
          spec: plan.spec,
          theme_key: plan.spec?.theme_key,
          step_map: plan.step_map,
          fee_charged: plan.fee_charged,
        },
        themes: prev?.themes || [],
        version_no: regenerate ? null : prev?.version_no || null,
      }));
      // Quality warnings that survived the server's bounded retry: name what the model did
      // not honor (a feature not integrated, a title not in Persian) instead of silence.
      setPlanWarnings(plan.warnings || null);
      // The MVP cut renders now; the user starts screen fills explicitly (approval gate).
    } catch (e) {
      setPhase(null);
      setPlanning(false);
      // A toast disappears; a failed generation must stay visible with its reasons
      // (e.g. "app structure contract violation" + validation_errors from the server).
      if (isCreditError(e)) { setPlanCredit(true); return; }
      setPlanError({
        message: e?.message || 'Generation failed',
        details: Array.isArray(e?.body?.validation_errors) ? e.body.validation_errors : [],
      });
      toast.error(e.message);
    }
  };

  const retryScreen = (id) => fillScreens([id]);
  const regenerateScreen = (id) => fillScreens([id], { regenerate: true });

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await api.appMockDownloadZip(projectId, effTheme || undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setDownloading(false); }
  };

  // The vertical pipeline (Ali's approved mock, 2026-08-16): one spine, one node per produced
  // box, horizontal timer chips on the active row, final-time chips on done rows.
  const designBox = pendingDesign && !planning ? (
    <SectionCard title={t('prototype.designTitle')} testId="prototype-pending-design">
      <p className="text-sm text-muted-foreground"><T k="prototype.pendingDesignHelp" /></p>
      <textarea
        rows={10}
        className="w-full text-sm border border-border rounded-md px-3 py-2 bg-card text-foreground resize-y leading-relaxed"
        dir="auto"
        value={designDraft}
        onChange={(e) => setDesignDraft(e.target.value)}
        data-testid="prototype-pending-design-edit"
      />
      {pendingDesign.pages?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="prototype-pending-pages">
          {pendingDesign.pages.map((p, i) => (
            <span key={p.name} className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border">
              {i + 1}. {p.name} · {p.block}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => generate(pendingDesign.regenerate, null, modelId, designDraft.trim(), pendingDesign.pages)}
          data-testid="prototype-build-from-design"
        >
          <ArrowRight className="h-4 w-4 me-2" /> {t('prototype.buildFromDesign')}
        </Button>
        <Button variant="outline" onClick={() => startDesign(pendingDesign.regenerate)} data-testid="prototype-redesign">
          <RefreshCw className="h-4 w-4 me-2" /> {t('prototype.redesign')}
        </Button>
      </div>
    </SectionCard>
  ) : spec?.design && !pendingDesign ? (
    <SectionCard title={t('prototype.designTitle')} testId="prototype-design-card">
      <p className="text-sm text-muted-foreground"><T k="prototype.designHelp" /></p>
      <textarea
        rows={10}
        className="w-full text-sm border border-border rounded-md px-3 py-2 bg-card text-foreground resize-y leading-relaxed"
        dir="auto"
        value={designDraft}
        onChange={(e) => setDesignDraft(e.target.value)}
        disabled={!!tasks || planning}
        data-testid="prototype-design-edit"
      />
      {designDraft.trim() !== (spec.design || '').trim() && (
        <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
          <p className="text-sm text-muted-foreground"><T k="prototype.designEditedHint" /></p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDesignDraft(spec.design || '')}
              disabled={!!tasks || planning} data-testid="prototype-design-revert">
              {t('prototype.designRevert')}
            </Button>
            <Button onClick={() => generate(false, null, modelId, designDraft.trim())}
              disabled={!!tasks || planning} data-testid="prototype-design-apply">
              {planning
                ? <Loader2 className="h-4 w-4 me-2 animate-spin" />
                : <Scissors className="h-4 w-4 me-2" />}
              {t('prototype.designApply')}
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  ) : phase === 'design' ? (
    <SectionCard testId="prototype-design-skeleton"><Skeleton className="h-28 w-full" /></SectionCard>
  ) : null;

  const structureBox = spec ? (
    <MvpCutCard
      key={JSON.stringify(spec.mvp_cut)}
      mvpCut={spec.mvp_cut}
      busy={!!tasks || planning}
      onReplan={(directives) => generate(false, directives)}
      onDirtyChange={setCutDirty}
    />
  ) : phase === 'structure' ? (
    <SectionCard testId="prototype-structure-skeleton"><Skeleton className="h-24 w-full" /></SectionCard>
  ) : null;

  const screensBox = spec ? (
    <>
      <ScreenProgress spec={spec} tasks={tasks} screenErrors={screenErrors} creditWait={creditWait}
        doneIds={bespokeDone} onRetry={retryScreen} />
      {unfilled.length > 0 && !tasks && (
        <SectionCard testId="prototype-fill-cta">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-muted-foreground">
              {cutDirty ? t('prototype.fillBlockedByCut') : t('prototype.fillHelp', { count: unfilled.length })}
            </p>
            <Button
              onClick={() => (mode === 'bespoke' ? runBespoke(bespokePartial) : fillScreens(unfilled))}
              disabled={cutDirty}
              data-testid="prototype-fill-button"
            >
              <Play className="h-4 w-4 me-2" />
              {mode === 'bespoke'
                ? bespokePartial
                  ? t('prototype.resumeBespoke', { count: bespokeRemaining })
                  : t('prototype.fillStartBespoke', { count: (spec?.screens || []).length })
                : t('prototype.fillStart', { count: unfilled.length })}
            </Button>
          </div>
        </SectionCard>
      )}
    </>
  ) : null;

  let body;
  if (loading) {
    body = <SectionCard testId="prototype-loading"><Skeleton className="h-40 w-full" /></SectionCard>;
  } else if (!spec && !pendingDesign && !planning) {
    body = <div className="text-muted-foreground text-sm px-2" data-testid="prototype-empty"><T k="prototype.empty" /></div>;
  } else {
    body = (
      <>
        <PipelineRail testId="prototype-rail">
          <RailRow stageKey="design" label={t('prototype.stageDesign')} status={designStatus}
            typicalSec={35} finalSec={finalTimes.design} micro={designMicro}>
            {designBox}
          </RailRow>
          <RailRow stageKey="structure" label={t('prototype.stageStructure')} status={structureStatus}
            typicalSec={50} finalSec={finalTimes.structure} micro={structureMicro}>
            {structureBox}
          </RailRow>
          <RailRow stageKey="screens" label={t('prototype.stageScreens')} status={screensStatus}
            typicalSec={spec?.screens?.length ? spec.screens.length * (mode === 'bespoke' ? 75 : 40) : 200}
            finalSec={finalTimes.screens} micro={screensMicro}
            progress={tasks ? {
              done: tasks.filter((x) => x.status === 'done').length,
              total: tasks.length,
            } : null}
            detail={(() => {
              const cur = tasks?.find((x) => x.status === 'active');
              if (!cur) return null;
              if (cur.id === '__shell__') return t('prototype.detailShell');
              const sc = (spec?.screens || []).find((x) => x.id === cur.id);
              return t('prototype.detailScreen', {
                name: sc?.title?.[spec?.primary_lang] || sc?.title?.en || cur.id,
              });
            })()}
            isLast>
            {screensBox}
          </RailRow>
        </PipelineRail>

        {versionNo && (
          <SectionCard
            title={t('prototype.previewTitle')}
            testId="prototype-preview-card"
            action={(
              <div className="flex flex-wrap items-center gap-3 justify-end">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-muted-foreground" />
                  <Select value={effTheme || ''} onValueChange={setPreviewTheme}>
                    <SelectTrigger className="w-44" data-testid="prototype-theme-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {themes.map((th) => (
                        <SelectItem key={th.key} value={th.key} data-testid={`prototype-theme-${th.key}`}>
                          {themeLabel(th, uiLang)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCompareTheme(compareTheme ? null : (themes.find((th) => th.key !== effTheme)?.key || effTheme))}
                  data-testid="prototype-compare-toggle"
                >
                  <Columns2 className="h-4 w-4 me-2" /> {compareTheme ? t('prototype.compareHide') : t('prototype.compare')}
                </Button>
                <Button variant="outline" onClick={exportPitch} disabled={exportingPitch} data-testid="prototype-pitch-button">
                  {exportingPitch ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Presentation className="h-4 w-4 me-2" />}
                  {t('prototype.pitchExport')}
                </Button>
                <Button onClick={downloadZip} disabled={downloading} data-testid="prototype-download-button">
                  {downloading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Download className="h-4 w-4 me-2" />}
                  {t('prototype.download')}
                </Button>
              </div>
            )}
          >
            <p className="text-xs text-muted-foreground" data-testid="prototype-honesty-note"><T k="prototype.mockNote" /></p>
            {previewLoading && <Skeleton className="h-96 w-full" data-testid="prototype-preview-loading" />}
            {!previewLoading && previewDoc && (
              <div className={compareTheme ? 'grid lg:grid-cols-2 gap-3' : ''}>
                <iframe
                  title={t('prototype.previewTitle')}
                  srcDoc={previewDoc}
                  sandbox="allow-scripts"
                  className="w-full h-[560px] border border-border rounded-xl bg-white"
                  data-testid="prototype-preview-iframe"
                />
                {compareTheme && (
                  <div className="flex flex-col gap-2" data-testid="prototype-compare-pane">
                    <Select value={compareTheme} onValueChange={setCompareTheme}>
                      <SelectTrigger className="w-44 self-start" data-testid="prototype-compare-theme-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {themes.map((th) => (
                          <SelectItem key={th.key} value={th.key}>{themeLabel(th, uiLang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {compareDoc
                      ? (
                        <iframe
                          title={`${t('prototype.previewTitle')} B`}
                          srcDoc={compareDoc}
                          sandbox="allow-scripts"
                          className="w-full h-[518px] border border-border rounded-xl bg-white"
                          data-testid="prototype-compare-iframe"
                        />
                      )
                      : <Skeleton className="h-[518px] w-full" />}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {(spec.screens || []).map((s) => (
                <Button
                  key={s.id}
                  size="sm"
                  variant="outline"
                  disabled={!!tasks}
                  onClick={() => regenerateScreen(s.id)}
                  data-testid={`prototype-regen-${s.id}`}
                >
                  <RefreshCw className="h-3.5 w-3.5 me-1.5" />
                  {t('prototype.regenScreen', { title: s.title?.[spec.primary_lang] || s.title?.en || s.id })}
                </Button>
              ))}
            </div>
          </SectionCard>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionCard
        title={projectName ? `${t('prototype.title')} — ${projectName}` : t('prototype.title')}
        testId="prototype-action-card"
        action={(
          <Button onClick={() => startDesign(!!spec)} disabled={!hasIdea || !!tasks || planning} data-testid="generate-prototype-button">
            {/* A disabled button must never be SILENT gray — it says what it is doing
                (Ali's approved mock, 2026-08-16). */}
            {(planning || tasks) ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {tasks
                  ? t('prototype.busyScreens', {
                    done: tasks.filter((x) => x.status === 'done').length,
                    total: tasks.length,
                  })
                  : t('prototype.busyWorking')}
              </>
            ) : (
              <>
                <Smartphone className="h-4 w-4 me-2" /> {spec ? t('prototype.regenerate') : t('prototype.generate')}
              </>
            )}
          </Button>
        )}
      >
        {!hasIdea && <p className="text-sm text-muted-foreground"><T k="prototype.needIdea" /></p>}
        <p className="text-sm text-muted-foreground"><T k="prototype.help" /></p>
        {/* Model choice is available BEFORE the first run (Ali, 2026-08-13) — the picker feeds
            every call of the run: structure, re-plans, screen fills. */}
        <div className="mt-1 flex flex-col gap-2">
          <ModelPicker
            value={modelId}
            onChange={setModelId}
            busy={!!tasks || planning}
            testId="prototype-model-picker"
            onModels={setModels}
          />
          <ModeCards
            mode={mode}
            onMode={setMode}
            models={models}
            modelId={modelId}
            onModel={setModelId}
            busy={!!tasks || planning}
          />
          {spec && (
            <ServedModelBadge
              model={servedModels?.['generate-app-mock-screen'] || servedModels?.['generate-app-mock']}
              testId="prototype-served-model"
            />
          )}
          {/* Before the first run the user can seed the design with a vision; once a design
              exists, editing THAT is the one and only steering surface (Ali, 2026-08-15 —
              two fields for one purpose was one too many). */}
          {!spec && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="prototype-vision">
                {t('prototype.visionLabel')}
              </label>
              <textarea
                id="prototype-vision"
                rows={2}
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-card text-foreground resize-y"
                placeholder={t('prototype.visionPh')}
                value={vision}
                onChange={(e) => setVision(e.target.value)}
                disabled={!!tasks || planning}
                data-testid="prototype-vision-input"
              />
            </div>
          )}
        </div>
      </SectionCard>

      {/* Out of credits mid-run: the LOUD, honest banner (approved mock). Paid screens stay
          done; blocked ones wait; resume builds only the unbuilt — no double billing. */}
      {creditWait.length > 0 && !tasks && !planning && (
        <SectionCard testId="prototype-credit-banner">
          <div className="bg-destructive-soft border border-destructive-border rounded-md p-4">
            <p className="text-sm font-semibold text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" /> {t('prototype.creditTitle')}
            </p>
            <p className="mt-1 text-sm text-destructive">
              {t('prototype.creditBody', {
                built: (spec?.screens || []).filter((s) => s.filled).length,
                waiting: creditWait.length,
              })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {/* The USER-facing credit page (settings), not the admin panel — end users top
                  up there; admins can grant themselves from the same place's links. */}
              <Button size="sm" onClick={() => window.open('/settings', '_blank')} data-testid="prototype-credit-topup">
                {t('prototype.creditTopUp')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => fillScreens(creditWait)} data-testid="prototype-credit-resume">
                {t('prototype.creditResume', { count: creditWait.length })}
              </Button>
            </div>
          </div>
        </SectionCard>
      )}

      {planWarnings && !planning && (
        <SectionCard testId="prototype-plan-warnings">
          <div className="bg-warning-soft border border-warning-border rounded-md p-4 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm">
              <p className="font-medium text-foreground"><T k="prototype.qualityWarnTitle" /></p>
              {planWarnings.missing_features?.length > 0 && (
                <p className="mt-1 text-muted-foreground">
                  {t('prototype.qualityMissing')} <span className="font-medium text-foreground">{planWarnings.missing_features.join(' · ')}</span>
                </p>
              )}
              {planWarnings.non_persian_titles?.length > 0 && (
                <p className="mt-1 text-muted-foreground"><T k="prototype.qualityScript" /></p>
              )}
              {planWarnings.too_few_screens && (
                <p className="mt-1 text-muted-foreground"><T k="prototype.qualityFewScreens" /></p>
              )}
              {planWarnings.design_too_short && (
                <p className="mt-1 text-muted-foreground"><T k="prototype.qualityShortDesign" /></p>
              )}
              {planWarnings.missing_pages?.length > 0 && (
                <p className="mt-1 text-muted-foreground">
                  {t('prototype.qualityMissingPages')} <span className="font-medium text-foreground">{planWarnings.missing_pages.join(' · ')}</span>
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground"><T k="prototype.qualityHint" /></p>
            </div>
          </div>
        </SectionCard>
      )}

      {planCredit && !planning && (
        <SectionCard testId="prototype-plan-credit">
          <CreditBanner testId="prototype-plan-credit-banner" onResume={() => startDesign(!!spec)} />
        </SectionCard>
      )}

      {planError && !planning && (
        <SectionCard testId="prototype-plan-error">
          <div className="bg-destructive-soft border border-destructive-border rounded-md p-4 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-destructive font-medium"><T k="prototype.planFailed" /></p>
              <p className="text-xs text-destructive font-data break-words">{planError.message}</p>
              {planError.details.length > 0 && (
                <ul className="mt-1 text-xs text-destructive font-data list-disc ms-4">
                  {planError.details.map((d) => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => startDesign(!!spec)} data-testid="prototype-plan-retry">
              {t('prototype.retryScreen')}
            </Button>
          </div>
        </SectionCard>
      )}

      {body}
    </div>
  );
}
